import { useTranslation } from 'react-i18next';
import type { SabotageKind } from '@il-colpo/engine';
import { Button, Panel } from '../../ui/index.ts';
import { alarmPercent } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import styles from './PowerBar.module.css';

const SABOTAGES: readonly SabotageKind[] = ['tamper', 'tipoff', 'rush'];

/** Powers and secret tools: Plan B, Loop, the drone, skipping an alcove, the Mole's sabotages. */
export function PowerBar() {
  const { t } = useTranslation('game');
  const { view, send, g } = useGame();
  const exec = view.exec;
  if (!exec) return null;
  const role = view.you.role;
  const powers = view.you.powers;
  const alcoveAhead = exec.stops.some(
    (s, i) => i > exec.stopIndex && s.optional && !s.skipped && s.status === 'pending',
  );
  const items: React.ReactNode[] = [];

  if (role === 'mastermind') {
    items.push(
      <Button
        key="planB"
        variant="secondary"
        disabled={!exec.planB?.canUndo || powers.planB <= 0}
        onClick={() => send({ type: 'power.use', power: 'planB' })}
      >
        {t('power.planB', { count: powers.planB })}
        {exec.planB?.canUndo ? ` (−${alarmPercent(exec.planB.tenths)}%)` : ''}
      </Button>,
    );
    if (exec.gear.includes('drone')) {
      items.push(
        <Button
          key="drone"
          variant="secondary"
          onClick={() => send({ type: 'gear.use', gear: 'drone' })}
        >
          {t('power.drone')}
        </Button>,
      );
    }
    if (alcoveAhead && view.game.phase === 'execution') {
      items.push(
        <Button key="skip" variant="ghost" onClick={() => send({ type: 'exec.skipOptional' })}>
          {t('power.skip')}
        </Button>,
      );
    }
  }
  if (role === 'hacker') {
    items.push(
      <Button
        key="loop"
        variant="secondary"
        disabled={powers.loops <= 0 || exec.looped}
        onClick={() => send({ type: 'power.use', power: 'loop' })}
      >
        {t('power.loop', { count: powers.loops })}
      </Button>,
    );
  }
  const sab = view.you.sabotagesLeft;
  const mole = sab
    ? SABOTAGES.filter((k) => sab[k]).map((k) => (
        <Button
          key={k}
          variant="danger"
          size="md"
          onClick={() => send({ type: 'sabotage.use', kind: k })}
        >
          {g(`sabotage.${k}`)}
        </Button>
      ))
    : [];

  if (items.length === 0 && mole.length === 0) return null;
  return (
    <Panel tone="night" title={t('power.title')} className={styles['bar']}>
      {items.length > 0 ? <div className={styles['row']}>{items}</div> : null}
      {mole.length > 0 ? (
        <div className={styles['mole']}>
          <p className={styles['moleTitle']}>{t('power.mole')}</p>
          <div className={styles['row']}>{mole}</div>
        </div>
      ) : null}
    </Panel>
  );
}
